import { defineStore } from 'pinia'
import { type Ref, ref } from 'vue'

import type {
  FrappeAuthForgetPasswordUsernameRequest,
  FrappeAuthTokenReturnValue,
  FrappeAuthUsernameResponse,
} from '@/plugins/vuePluginFrappe'

import {
  type FrappeAuthInstance,
  type FrappeAuthRequest,
  type FrappeAuthResponse,
  getFrappeAuthInstance,
  transformFrappeErrorObject,
} from '@/utilities/stores'

type AuthInstanceAuthTokenType = FrappeAuthInstance['tokenType']
type AuthInstanceAxiosInstance = FrappeAuthInstance['axios']
type AuthInstanceUseAuthToken = FrappeAuthInstance['useToken']

type UserAuthRequest = FrappeAuthRequest
type UserAuthResponse = FrappeAuthResponse
type UserAuthUsernameResponse = FrappeAuthUsernameResponse
type UserResetPasswordUsername = FrappeAuthForgetPasswordUsernameRequest

type GetAuthInstanceAuthTokenFunction = () => FrappeAuthTokenReturnValue
type GetAuthUsernameFunction = () => Promise<UserAuthUsernameResponse>
type LoginFunction = (userAuthCredentials: UserAuthRequest) => Promise<UserAuthResponse>
type LogoutFunction = () => Promise<void>
type ResetPasswordFunction = (username: UserResetPasswordUsername) => Promise<void>

interface FrappeAuthAPIStore {
  authInstanceAuthTokenType: Ref<AuthInstanceAuthTokenType>
  authInstanceAxiosInstance: Ref<AuthInstanceAxiosInstance>
  getAuthInstanceAuthToken: GetAuthInstanceAuthTokenFunction
  getAuthUsername: GetAuthUsernameFunction
  isAuthInstanceUseAuthToken: Ref<AuthInstanceUseAuthToken>
  login: LoginFunction
  logout: LogoutFunction
  resetPassword: ResetPasswordFunction
}

/**
 * ! Caution:
 * * all "frappeInstance" function calls must be wrapped by
 * * "transformFrappeErrorObject" function, for consistent error handling
 */

const useFrappeAuthAPIStore = defineStore('frappeAuthAPI', (): FrappeAuthAPIStore => {
  const frappeAuthInstance: FrappeAuthInstance = getFrappeAuthInstance()

  const authInstanceAuthTokenType = ref<AuthInstanceAuthTokenType>(frappeAuthInstance.tokenType)
  const authInstanceAxiosInstance = ref<AuthInstanceAxiosInstance>(frappeAuthInstance.axios)
  const isAuthInstanceUseAuthToken = ref<AuthInstanceUseAuthToken>(frappeAuthInstance.useToken)

  const getAuthUsername: GetAuthUsernameFunction = async () => {
    return await transformFrappeErrorObject(async () => {
      return await frappeAuthInstance.getLoggedInUser()
    })
  }

  const getAuthInstanceAuthToken: GetAuthInstanceAuthTokenFunction = () => {
    let authInstanceTokenValue: string | undefined = undefined

    if (frappeAuthInstance.token) {
      authInstanceTokenValue = frappeAuthInstance.token()
    }

    return authInstanceTokenValue
  }

  const login: LoginFunction = async (userAuthCredentials) => {
    return await transformFrappeErrorObject(async () => {
      return await frappeAuthInstance.loginWithUsernamePassword(userAuthCredentials)
    })
  }

  const logout: LogoutFunction = async () => {
    await transformFrappeErrorObject(async () => {
      await frappeAuthInstance.logout()
    })
  }

  const resetPassword: ResetPasswordFunction = async (username) => {
    await transformFrappeErrorObject(async () => {
      await frappeAuthInstance.forgetPassword(username)
    })
  }

  return {
    authInstanceAuthTokenType,
    authInstanceAxiosInstance,
    getAuthInstanceAuthToken,
    getAuthUsername,
    isAuthInstanceUseAuthToken,
    login,
    logout,
    resetPassword,
  }
})

export {
  useFrappeAuthAPIStore,
  type UserAuthRequest,
  type UserAuthResponse,
  type UserAuthUsernameResponse,
}
